import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import io from 'socket.io-client';
import ReactMarkdown from 'react-markdown';
import { MapPin, Menu, Mic, MoveUpRight } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';

// Styled components for the signup form
const SignupContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 16px;
    min-height: 100vh;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const SignupModal = styled.div`
  width: 647px;
  background: #000000;
  border-radius: 25px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 500px;
    height: auto;
    min-height: 700px;
    padding: 32px 24px;
    border-radius: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 24px 20px;
    border-radius: 16px;
    min-height: 650px;
  }
  
  @media (max-width: 360px) {
    padding: 20px 16px;
    border-radius: 12px;
    min-height: 600px;
  }
`;

const SignupHeader = styled.div`
  margin-bottom: 28px;
  
  @media (max-width: 768px) {
    margin-bottom: 32px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 24px;
  }
  
  h2 {
    color: #ffffff;
    font-size: 25px;
    font-weight: 600;
    margin: 0;
    text-align: center;
    
    @media (max-width: 768px) {
      font-size: 22px;
    }
    
    @media (max-width: 480px) {
      font-size: 20px;
    }
    
    @media (max-width: 360px) {
      font-size: 18px;
    }
  }
`;

const SignupForm = styled.form`
  width: 100%;
  max-width: 460px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 28px;
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
  
  label {
    display: block;
    color: #ffffff;
    font-size: 16px;
    margin-bottom: 8px;
    font-weight: 500;
    
    @media (max-width: 768px) {
      font-size: 15px;
      margin-bottom: 6px;
    }
    
    @media (max-width: 480px) {
      font-size: 14px;
      margin-bottom: 5px;
    }
  }
`;

const FormInput = styled.input`
  width: 100%;
  height: 46px;
  background: rgba(217, 217, 217, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 0 15px;
  color: #ffffff;
  font-size: 16px;
  outline: none;
  transition: border-color 0.3s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
  
  &:focus {
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  @media (max-width: 768px) {
    height: 50px;
    padding: 0 16px;
    font-size: 15px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    height: 45px;
    padding: 0 14px;
    font-size: 14px;
    border-radius: 8px;
  }
  
  @media (max-width: 360px) {
    height: 42px;
    padding: 0 12px;
    font-size: 13px;
    border-radius: 6px;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 17px;
  margin-bottom: 28px;
  
  @media (max-width: 768px) {
    gap: 12px;
    margin-bottom: 24px;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0;
    margin-bottom: 20px;
  }
`;

const FormGroupRow = styled(FormGroup)`
  flex: 1;
  margin-bottom: 0;
  
  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`;

const SignupButton = styled.button`
  width: 100%;
  height: 50px;
  background: #F43F60;
  border: none;
  border-radius: 12px;
  color: #EDEDED;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover:not(:disabled) {
    background: #E03A56;
  }
  
  &:disabled {
    background: #666666;
    cursor: not-allowed;
    opacity: 0.7;
  }
  
  @media (max-width: 768px) {
    height: 48px;
    font-size: 17px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    height: 45px;
    font-size: 16px;
    border-radius: 8px;
  }
  
  @media (max-width: 360px) {
    height: 42px;
    font-size: 15px;
    border-radius: 6px;
  }
`;

const Divider = styled.div`
  width: 100%;
  max-width: 420px;
  text-align: center;
  margin: 30px 0;
  position: relative;
  
  @media (max-width: 768px) {
    max-width: 100%;
    margin: 24px 0;
  }
  
  @media (max-width: 480px) {
    margin: 20px 0;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: rgba(255, 255, 255, 0.2);
  }
  
  span {
    background: #000000;
    color: #9E9E9E;
    padding: 0 20px;
    font-size: 16px;
    position: relative;
    z-index: 1;
    
    @media (max-width: 768px) {
      font-size: 15px;
      padding: 0 16px;
    }
    
    @media (max-width: 480px) {
      font-size: 14px;
      padding: 0 12px;
    }
  }
`;

const SocialLogin = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    gap: 12px;
    margin-bottom: 24px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }
`;

const SocialButton = styled.button`
  width: 100px;
  height: 53px;
  background: #363636;
  border: none;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background: #404040;
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
  

  @media (max-width: 768px) {
    width: auto;
    height: 50px;
    padding: 0 16px;
  }
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: auto;
  
  @media (max-width: 768px) {
    margin-top: 20px;
  }
  
  @media (max-width: 480px) {
    margin-top: 16px;
  }
  
  span {
    color: #D4D4D4;
    font-size: 16px;
    margin-right: 10px;
    
    @media (max-width: 768px) {
      font-size: 15px;
      margin-right: 8px;
    }
    
    @media (max-width: 480px) {
      font-size: 14px;
      margin-right: 6px;
    }
  }
  
  .login-link {
    color: #F43F60;
    text-decoration: none;
    font-size: 16px;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
    
    @media (max-width: 768px) {
      font-size: 15px;
    }
    
    @media (max-width: 480px) {
      font-size: 14px;
    }
  }
`;

const ErrorMessage = styled.div`
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid #F43F60;
  color: #F43F60;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    margin-bottom: 16px;
    font-size: 13px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 12px;
    margin-bottom: 12px;
    font-size: 12px;
    border-radius: 4px;
  }
`;

const SuccessMessage = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid #22C55E;
  color: #22C55E;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    margin-bottom: 16px;
    font-size: 13px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 12px;
    margin-bottom: 12px;
    font-size: 12px;
    border-radius: 4px;
  }
`;



const Signup = () => {
  const navigate = useNavigate();

  // Signup form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    userInfo: {
      name: '',
      phone: '',
      address: '',
      city: ''
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (error) setError('');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(process.env.REACT_APP_API_URL + '/api/auth/user/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          userInfo: formData.userInfo
        })
      });

      const data = await response.json();
      console.log(data);

      if (response.ok) {
        setSuccess('Signup successful! Please check your email to verify your account.');
        // Store token in sessionStorage
        sessionStorage.setItem('token', data.data.token);
        sessionStorage.setItem('user', JSON.stringify(data.data.user));

        // Redirect to dashboard or home page
        setTimeout(() => {
          window.location.href = '/user/dashboard'; // or wherever you want to redirect
        }, 1500);
      } else {
        setError(data.message || 'Signup failed. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      console.error('Signup error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // OAuth handlers
  const handleGoogleSuccess = async (credentialResponse) => {
    setIsOAuthLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(process.env.REACT_APP_API_URL + '/api/auth/google', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          idToken: credentialResponse.credential,
          userInfo: {}
        })
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        setSuccess('Google signup successful!');
        // Store token in sessionStorage
        sessionStorage.setItem('token', data.data.token);
        sessionStorage.setItem('user', JSON.stringify(data.data.user));

        // Redirect to dashboard or home page
        setTimeout(() => {
          window.location.href = '/user/dashboard';
        }, 1500);
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error('Google signup error:', error);
      setError(error.message || 'Google signup failed. Please try again.');
    } finally {
      setIsOAuthLoading(false);
    }
  };

  const handleGoogleError = (error) => {
    console.error('Google signup error:', error);
    setError('Google signup failed. Please try again.');
  };


  // --- UI ---
  return (
    <>
      {/* Main Content */}
      <main className="main-content">
        <section className="search-area home" style={{ backgroundImage: 'url(/home-bg.png)', backgroundSize: 'cover', backgroundPosition: 'top', backgroundRepeat: 'no-repeat' }}>
          <div className="containers">
            <div className="row justify-content-center" >
              <div className="col-md-12 col-lg-10 col-xl-10" >

                <SignupContainer>
                  <SignupModal>
                    <SignupHeader>
                      <h2>User Sign up</h2>
                    </SignupHeader>

                    <SignupForm onSubmit={handleSubmit}>
                      {error && (
                        <ErrorMessage>
                          {error}
                        </ErrorMessage>
                      )}

                      {success && (
                        <SuccessMessage>
                          {success}
                        </SuccessMessage>
                      )}



                      <FormGroup>
                        <label htmlFor="name">Full name</label>
                        <FormInput
                          type="text"
                          id="name"
                          name="userInfo.name"
                          value={formData.userInfo.name}
                          onChange={handleInputChange}
                          placeholder="Enter your full name"
                          required
                        />
                      </FormGroup>

                      <FormGroup>
                        <label htmlFor="email">Email</label>
                        <FormInput
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="Enter your email"
                          required
                        />
                      </FormGroup>

                      <FormRow>
                        <FormGroupRow>
                          <label htmlFor="phone">Phone</label>
                          <FormInput
                            type="tel"
                            id="phone"
                            name="userInfo.phone"
                            value={formData.userInfo.phone}
                            onChange={handleInputChange}
                            placeholder="Enter phone number"
                            required
                          />
                        </FormGroupRow>

                        <FormGroupRow>
                          <label htmlFor="city">City</label>
                          <FormInput
                            type="text"
                            id="city"
                            name="userInfo.city"
                            value={formData.userInfo.city}
                            onChange={handleInputChange}
                            placeholder="Enter your city"
                            required
                          />
                        </FormGroupRow>
                      </FormRow>

                      <FormGroup>
                        <label htmlFor="address">Address</label>
                        <FormInput
                          type="text"
                          id="address"
                          name="userInfo.address"
                          value={formData.userInfo.address}
                          onChange={handleInputChange}
                          placeholder="Enter your address"
                          required
                        />
                      </FormGroup>

                      <FormGroup>
                        <label htmlFor="password">Password</label>
                        <FormInput
                          type="password"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="Enter your password"
                          required
                        />
                      </FormGroup>

                      <FormGroup>
                        <label htmlFor="confirmPassword">Re-type Password</label>
                        <FormInput
                          type="password"
                          id="confirmPassword"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          placeholder="Confirm your password"
                          required
                        />
                      </FormGroup>

                      <SignupButton type="submit" disabled={isLoading || isOAuthLoading}>
                        {isLoading ? 'Creating account...' : 'Sign up'}
                      </SignupButton>
                    </SignupForm>

                    <Divider>
                      <span>Or</span>
                    </Divider>

                    <SocialLogin>
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        useOneTap={false}
                        theme="outline"
                        size="large"
                        width="115"
                        text="signup_with"
                        shape="rectangular"
                        logo_alignment="left"
                        disabled={isOAuthLoading}
                      />

                    </SocialLogin>

                    <LoginLink>
                      <span>Already have an account?</span>
                      <Link to="/user/login" className="login-link">Log in</Link>
                    </LoginLink>
                  </SignupModal>
                </SignupContainer>
              </div>
            </div>
          </div>
        </section>
      </main>
      {/* Footer */}
      <footer className="footer-area">
        <div className="containers">
          <div className="row">
            <div className="col-md-6">
              <div className="copy">
                <p>Copyright © 2024 Automotiveai.com</p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="trams">
                <p><a href="#">Privacy Policy</a> | <a href="#">Terms & Conditions</a></p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12">
          <a href="#" className="scrolltotop"><i className="bi bi-arrow-up-circle"></i></a>
        </div>
      </footer>
    </>
  );
};

export default Signup; 